import React, { useEffect, useState } from 'react';
import { Dropdown, SEVERITY, TextInput, Tooltip } from '@silverfort/sf-components';
import { INCIDENT_SEVERITY } from '@silverfort-nodelibs/common/utils/consts';

export const NumberInput = ({
    value,
    displayName,
    description,
    schema,
    onChange,
    onValidityChange
}: {
    value: string;
    displayName: string;
    description?: string;
    schema: { minimum?: number; maximum?: number };
    onChange: (v: string) => void;
    onValidityChange?: (isValid: boolean) => void;
}) => {
    const [error, setError] = useState<string | null>(null);
    const [touched, setTouched] = useState(false);
    const [isValid, setIsValid] = useState(true);

    useEffect(() => {
        let err: string | null = null;
        const num = Number(value);
        if (schema.minimum !== undefined && num < schema.minimum) {
            err = `Value must be ≥ ${schema.minimum}`;
        } else if (schema.maximum !== undefined && num > schema.maximum) {
            err = `Value must be ≤ ${schema.maximum}`;
        }
        setError(err);
        const valid = !err;
        if (touched && valid !== isValid) {
            setIsValid(valid);
            if (onValidityChange) onValidityChange(valid);
        }
    }, [value, schema, isValid, onValidityChange, touched]);

    return (
        <div className="flex flex-col">
            <div className="flex items-center gap-6">
                <Tooltip title={description}>
                    <label className="text-13 font-medium items-center flex h-8 text-sf-gray-800 text-nowrap w-44 shrink-0 mb-0">
                        {displayName}
                    </label>
                </Tooltip>
                <TextInput
                    wrapperClassName="!w-24 flex postion-unset"
                    className="h-8 px-3 py-2 !w-24"
                    type="number"
                    value={value}
                    min={schema.minimum}
                    max={schema.maximum}
                    required
                    onChange={(e) => {
                        setTouched(true);
                        onChange(e);
                    }}></TextInput>
            </div>
            {touched && error && <div className="text-xs text-red-500 mt-1">{error}</div>}
        </div>
    );
};

export const StringInput = ({
    value,
    displayName,
    description,
    schema,
    onChange,
    onValidityChange
}: {
    value: string;
    displayName: string;
    description?: string;
    schema: { minLength?: number; maxLength?: number };
    onChange: (v: string) => void;
    onValidityChange?: (isValid: boolean) => void;
}) => {
    const [error, setError] = useState<string | null>(null);
    const [touched, setTouched] = useState(false);
    const [isValid, setIsValid] = useState(true);

    useEffect(() => {
        let err: string | null = null;
        if (value && schema.minLength !== undefined && value.length < schema.minLength) {
            err = `Length must be ≥ ${schema.minLength}`;
        } else if (schema.maxLength !== undefined && value.length > schema.maxLength) {
            err = `Length must be ≤ ${schema.maxLength}`;
        }
        setError(err);
        const valid = !err;
        if (touched && valid !== isValid) {
            setIsValid(valid);
            if (onValidityChange) onValidityChange(valid);
        }
    }, [value, schema, isValid, onValidityChange, touched]);

    return (
        <div className="flex flex-col">
            <div className="flex items-center gap-6">
                <Tooltip title={description}>
                    <label className="text-13 font-medium items-center flex h-8 text-sf-gray-800 text-nowrap w-44 shrink-0 mb-0">
                        {displayName}
                    </label>
                </Tooltip>
                <TextInput
                    wrapperClassName="!w-24 flex "
                    className="h-8 px-3 py-2 !w-24"
                    type="text"
                    value={value}
                    minLength={schema.minLength}
                    maxLength={schema.maxLength}
                    required
                    onChange={(value) => {
                        setTouched(true);
                        onChange(value);
                    }}
                />
            </div>
            {touched && error && <div className="text-xs text-red-500 mt-1">{error}</div>}
        </div>
    );
};

const SEVERITY_OPTIONS = [
    { value: INCIDENT_SEVERITY.CRITICAL.value, title: 'Critical' },
    { value: INCIDENT_SEVERITY.HIGH.value, title: 'High' },
    { value: INCIDENT_SEVERITY.MEDIUM.value, title: 'Medium' },
    { value: INCIDENT_SEVERITY.LOW.value, title: 'Low' }
];

export const SeverityInput = ({
    value,
    displayName,
    onChange,
    onValidityChange
}: {
    value: number;
    displayName: string;
    onChange: (v: SEVERITY) => void;
    onValidityChange?: (isValid: boolean) => void;
}) => {
    const [error, setError] = useState<string | null>(null);
    const [touched, setTouched] = useState(false);
    const [isValid, setIsValid] = useState(true);

    useEffect(() => {
        let err: string | null = null;
        if (!value) err = 'Severity cannot be empty';
        setError(err);
        const valid = !err;
        if (touched && valid !== isValid) {
            setIsValid(valid);
            if (onValidityChange) onValidityChange(valid);
        }
    }, [value, isValid, onValidityChange, touched]);

    return (
        <div className="flex flex-col">
            <div className="flex items-center gap-6">
                <label className="text-13 font-medium items-center flex h-8 text-sf-gray-800 text-nowrap w-44 shrink-0 mb-0">
                    {displayName}
                </label>
                <Dropdown
                    items={SEVERITY_OPTIONS}
                    variant="trigger"
                    onChange={(value) => {
                        setTouched(true);
                        onChange(Array.isArray(value) && value.length ? value[0] : (value as unknown as SEVERITY));
                    }}
                    selectedItems={value}
                    size="small"
                    width="w-24"
                    className="!max-w-24 h-8 w-24"
                    text="Severity"
                />
            </div>
            {touched && error && <div className="text-xs text-red-500 mt-1">{error}</div>}
        </div>
    );
};
